<template>
  <div :class="{ 'c-dark-theme':  $store.state.darkMode, animated: false, fadeIn: true }">
    <!-- 배치작업 설정 --------------------------------------------------------------------------------------------->
    <CCard border-color="info" style="min-height: 600px">
      <CCardHeader>
        <BRow>
          <BCol>
            <BIconAppIndicator class="mr-2"/> <strong> 배치 작업정보 </strong>

            <!-- 배치서버 실행 상태 요약 (/api/job/status) -->
            <BBadge class="ml-2" variant="info">크론등록 {{ jobStatus.registeredCount }}</BBadge>
            <BBadge class="ml-1" variant="warning" v-if="jobStatus.runningCount">
              실행중 {{ jobStatus.runningCount }}
            </BBadge>
            <BBadge class="ml-1" variant="danger" v-if="jobStatus.errorCount">
              에러 {{ jobStatus.errorCount }}
            </BBadge>
            <BBadge class="ml-1" variant="secondary" v-if="jobStatusError" :title="jobStatusError">
              배치서버 응답없음
            </BBadge>
          </BCol>
          <BCol class="text-right">
            <BButtonGroup>
              <BButton size="sm" variant="info" class="mr-1" @click="getJob">
                <BIconArrowClockwise/>
              </BButton>
              <BButton size="sm" variant="warning" class="mr-3" @click="addJob">
                <BIconNodePlusFill/> 작업 추가
              </BButton>
              <BButton size="sm" variant="primary" type="submit" @click="applyJob" :disabled="isApplyJob">
                <BIconBootstrapReboot/> 설정 반영
              </BButton>
            </BButtonGroup>
          </BCol>
        </BRow>
      </CCardHeader>

      <CCardBody>

        <BTable
          small
          bordered
          responsive
          selectable
          select-mode="single"
          selected-variant="danger"
          sticky-header
          sticky-column
          ref="jobTable"
          thead-class="text-center"
          head-variant="light"
          class="text-nowrap small text-center"
          :fields="jobFields"
          :items="jobRows"
          :tbody-tr-class="jobRowClass"
          @row-selected="jobSelected"
        >
          <template #cell(status)="{item}">
            <BBadge class="small" :variant="statusVariant(item.status)">{{statusName(item.status)}}</BBadge>
          </template>

          <!-- 사용여부. 목록에서 한눈에 구분되도록 ON/OFF 배지로 표시한다 -->
          <template #cell(enabled)="{item}">
            <BBadge v-if="item.enabled" class="small px-2" variant="success">ON</BBadge>
            <BBadge v-else class="small px-2" variant="secondary">OFF</BBadge>
          </template>

          <!-- 전송 암호화 여부. ims-* 전송 배치에만 의미가 있어 그 외 작업은 '-'로 표시 -->
          <template #cell(encSend)="{item}">
            <template v-if="isPushJob(item)">
              <BBadge v-if="item.encSend===false" class="small" variant="danger">평문</BBadge>
              <BBadge v-else class="small" variant="success">암호화</BBadge>
            </template>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- 크론 등록 여부. 사용중(enabled)인데 미등록이면 작업파일 누락이나 잘못된 크론식이다 -->
          <template #cell(scheduled)="{item}">
            <BBadge v-if="item.scheduled===true" class="small" variant="success">등록</BBadge>
            <BBadge v-else-if="item.scheduled===false" class="small" variant="secondary">미등록</BBadge>
            <span v-else>-</span>
          </template>

          <!-- 실행 경과 시간. 작업일정 주기보다 크면 다음 회차가 건너뛰어지고 있다는 뜻 -->
          <template #cell(runningSec)="{item}">
            <BBadge v-if="item.runningSec!==null && item.runningSec!==undefined" class="small" variant="warning">
              {{ item.runningSec }}초 경과
            </BBadge>
            <span v-else>-</span>
          </template>

        </BTable>

        <BForm @submit="jobSubmit" autocomplete="off">
          <BCard class="p-2 small mb-0" v-if="job!==null" border-variant="info" no-body>

            <!-- jobName -->
            <BFormGroup
              size="sm" class="mb-1"
              label="작업 명"
              label-for="jobName"
              label-cols-md="3"
              valid-feedback="사용 가능한 작업명입니다"
              invalid-feedback="예) 재고데이터 전송작업"
              :state="job.name.length > 2"
            >
              <BFormInput size="sm" id="jobName"
                          v-model="job.name"
                          :state="job.name.length > 2"
                          maxLength="50"
                          required
                          trim/>
            </BFormGroup>
            <!-- description -->
            <BFormGroup
              size="sm" class="mb-1"
              label="작업 설명"
              label-for="jobDesc"
              label-cols-md="3"
            >
              <BFormInput size="sm" id="jobDesc"
                          v-model="job.description"
                          maxLength="200"
                          trim/>
            </BFormGroup>
            <!-- displayName : 상단 헤더의 접속상태 배지에 쓰이는 짧은 이름 -->
            <BFormGroup
              size="sm" class="mb-1"
              label="표시명"
              label-for="displayName"
              label-cols-md="3"
              valid-feedback="사용 가능한 표시명입니다"
              invalid-feedback="20자 이내로 입력 예) 재고전송"
              :state="isValidDisplayName(job.displayName)"
            >
              <BFormInput size="sm" id="displayName"
                          v-model="job.displayName"
                          :state="isValidDisplayName(job.displayName)"
                          maxLength="20"
                          trim/>
              <BFormText>
                화면 상단의 서버 접속상태 배지에 표시됩니다. 비워두면 작업 설명이 대신 표시됩니다.
              </BFormText>
            </BFormGroup>
            <!-- jobId -->
            <BFormGroup
              size="sm" class="mb-1"
              label="작업 코드"
              label-for="jobId"
              label-cols-md="3"
              valid-feedback="사용 가능한 코드입니다"
              invalid-feedback="영문 대문자/숫자/-/_ 30자 이내 예) IMS-PUSH-01"
              :state="isValidJobId(job.jobId)"
            >
              <BFormInput size="sm" id="jobId"
                          v-model="job.jobId"
                          @input="v=>(job.jobId = v.toUpperCase())"
                          :state="isValidJobId(job.jobId)"
                          maxLength="30"
                          required
                          trim/>
            </BFormGroup>
            <!-- jobEnabled -->
            <BFormGroup
              size="sm" class="mb-1"
              label="실행여부"
              label-for="jobEnabled"
              label-cols-md="3"
            >
              <BFormCheckbox id="jobEnabled"
                             class="mt-1"
                             v-model="job.enabled" switch>
                {{`${job.enabled?'작업 실행':'실행 중지'}`}}
              </BFormCheckbox>
            </BFormGroup>
            <div v-show="job.enabled">
              <!-- scheduled -->
              <BFormGroup
                size="sm" class="mb-0"
                label="실행 일정 설정"
                label-for="scheduled"
                label-cols-md="3"
              >
                <BFormCheckbox id="scheduled"
                               class="mt-1"
                               v-model="job.scheduled" switch>
                  {{`${job.scheduled?'정해진 일정 실행':'특정 이벤트 발생시 실행'}`}}
                </BFormCheckbox>
              </BFormGroup>
              <div v-show="job.scheduled" class="small">
                <VueCronEditorBuefy v-model="job.runSchedule"
                                    class="small font-weight-bold text-nowrap"
                                    locale="ko"
                                    :custom-locales="locale"

                />
              </div>
              <!-- eventName -->
              <BFormGroup v-show="!job.scheduled"
                          size="sm" class="mb-1"
                          label="이벤트 명"
                          label-for="eventName"
                          label-cols-md="3"
              >
                <BFormInput size="sm" id="eventName"
                            v-model="job.eventName"
                            maxLength="40"
                            trim/>
              </BFormGroup>
              <!-- dbType -->
              <CSelect size="sm" class="mb-1"
                       horizontal
                       label="작업 형태"
                       :value.sync="job.dbType"
                       :options="dbTypeOpts"
              />
              <!-- serverIp -->
              <BFormGroup
                size="sm" class="mb-1"
                label="서버 IP 주소"
                label-for="serverIp2"
                label-cols-md="3"
                valid-feedback="IP 주소 형식입니다"
                invalid-feedback="IP 주소  예) 192.168.10.123"
                :state="isValidExp(job.serverIp, 'ip')"
              >
                <BFormInput size="sm" id="serverIp2"
                            v-model="job.serverIp"
                            :state="isValidExp(job.serverIp, 'ip')"
                            maxLength="40"
                            trim/>
              </BFormGroup>
              <!-- baseUrl -->
              <BFormGroup
                size="sm" class="mb-1"
                label="Base URL"
                label-for="baseURL"
                label-cols-md="3"
                valid-feedback="URL 형식 입니다"
                invalid-feedback="URL 예) http://192.168.10.123:8080"
                :state="isValidExp(job.baseUrl, 'url')"
              >
                <BFormInput size="sm" id="baseURL"
                            v-model="job.baseUrl"
                            :state="isValidExp(job.baseUrl, 'url')"
                            maxLength="400"
                            trim/>
              </BFormGroup>

              <BFormGroup
                size="sm" class="mb-1"
                label="API 경로"
                label-for="apiURL"
                label-cols-md="3"
              >
                <BFormInput size="sm" id="apiPath"
                            v-model="job.apiPath"
                            maxLength="400"
                            trim/>
              </BFormGroup>
              <BFormGroup
                size="sm" class="mb-1"
                label="인증 경로"
                label-for="authPath"
                label-cols-md="3"
              >
                <BFormInput size="sm" id="authPath"
                            v-model="job.authPath"
                            maxLength="400"
                            trim/>
              </BFormGroup>
              <!-- port# -->
              <BFormGroup
                size="sm"  class="mb-1"
                label="서버 포트 번호"
                label-for="portNo"
                label-cols-md="3"
                valid-feedback="포트번호 형식입니다."
                invalid-feedback="포트번호 80 ~ 65535 사이 값"
                :state="isValidPort(job.port)"
              >
                <BFormInput size="sm"
                            id="portNo"
                            name="port"
                            type="number"
                            min="80"
                            max="65535"
                            :state="isValidPort(job.port)"
                            v-model="job.port"
                />
              </BFormGroup>
              <!-- dbName -->
              <BFormGroup
                size="sm" class="mb-1"
                label="사이트 코드"
                label-for="siteCode"
                label-cols-md="3"
              >
                <BFormInput size="sm" id="siteCode"
                            v-model="job.siteCode"
                            maxLength="40"
                            trim/>
              </BFormGroup>
              <!-- account -->
              <BFormGroup
                size="sm" class="mb-1"
                label="서버 접속 계정"
                label-for="account"
                label-cols-md="3"
              >
                <BFormInput size="sm" id="account"
                            autocomplete="nope"
                            v-model="job.account"
                            maxLength="40"
                            trim/>
              </BFormGroup>
              <!-- password -->
              <BFormGroup
                size="sm" class="mb-1"
                label="접속 비밀번호"
                label-for="jobPasswd"
                label-cols-md="3"
              >
                <BFormInput size="sm" id="jobPasswd"
                            type="password"
                            autocomplete="nope"
                            v-model="job.password"
                            maxLength="40"
                            trim/>
              </BFormGroup>
              <!-- auth-token -->
              <BFormGroup
                v-if="false"
                size="sm" class="mb-1"
                label="접속 토큰정보"
                label-for="authToken"
                label-cols-md="3"
              >
                <BFormInput size="sm" id="authToken" type="password"
                            v-model="job.authToken"
                            maxLength="40"
                            trim/>
              </BFormGroup>
              <!-- encSend : 상위(통합모니터링) 서버 전송 배치(ims-*) 전용 설정 -->
              <BFormGroup
                size="sm" class="mb-1"
                label="전송 암호화"
                label-for="encSend"
                label-cols-md="3"
              >
                <BFormCheckbox id="encSend"
                               class="mt-1"
                               v-model="job.encSend" switch>
                  {{ job.encSend === false ? '평문 전송' : '암호화 전송' }}
                </BFormCheckbox>
                <BFormText>
                  상위서버 전송 배치(<code>ims-*</code>)에만 적용됩니다.
                  <span v-if="job.encSend === false" class="text-danger">
                    평문으로 전송되므로 전송 구간 보호가 필요한 환경에서는 사용하지 마세요.
                  </span>
                  <span v-else>
                    암호모듈이나 암호화키를 사용할 수 없으면 평문으로 내리지 않고 전송을 중단합니다.
                  </span>
                </BFormText>
              </BFormGroup>
              <!-- auth-token -->
              <BFormGroup
                size="sm" class="mb-1"
                label="비고"
                label-for="remark"
                label-cols-md="3"
              >
                <BFormInput size="sm" id="remark"
                            v-model="job.remark"
                            maxLength="40"
                            trim/>
              </BFormGroup>
            </div>





            <div class="row justify-content-between p-3">
              <BButtonGroup>
                <BButton type="submit" variant="primary">
                  <BIconSaveFill/> 설정 저장
                </BButton>
                <BButton type="button" variant="info" class="ml-3" @click="showJobLogModal(job.jobId)">
                  <BIconCardList/> 로그 보기
                </BButton>
              </BButtonGroup>

              <BButton variant="danger" @click="deleteRecord">
                <BIconTrashFill/>
                삭 제
              </BButton>
            </div>
          </BCard>
        </BForm>
      </CCardBody>
    </CCard>


    <BModal id="job-log"
            ref="jobLog"
            size="xl"
            header-bg-variant="dark"
            header-text-variant="light"
            body-bg-variant="secondary"
            body-text-variant="dark"
            footer-bg-variant="dark"
            footer-text-variant="dark"
            scrollable>


      <template #modal-title>
        <div class="c-dark-theme">
        <BInputGroup>
          <template #append>
            <strong class="mr-3">
              배치작업 로그조회
            </strong>

            <BFormSelect :options="jobOpts"
                         size="sm"
                         @input="getJobLog(jobId)"
                         v-model="jobId" class="theme-select"/>

            <BFormInput size="sm" class="float-right" type="date" v-model="fromDts"></BFormInput>
            <BFormInput size="sm" class="float-right"  type="date" v-model="toDts"></BFormInput>
            <BButton size="sm" variant="info" @click="getJobLog(jobId)"> <BIconSearch/> 검 색</BButton>

          </template>



        </BInputGroup>
        </div>
      </template>

      <BTable small sticky-header="700px" selectable hover responsive bordered dark
              class="text-nowrap small"
              thead-class="text-center small"
              ref="jobLogTable"
              id="jobLogTable"
              select-mode="single"
              style="min-height:700px"
              selectedVariant="success"
              :per-page="logPerPage"
              :current-page="logCurrPage"
              :items="jobLogs"
              :fields="logFields"
              >


        <template #cell(log)="{value}">
          <BBadge :variant="value.code===200||value.code===0? 'success':'danger'">
            {{ value.code===200 || value.code===0 ?'성공':'실패'}}
          </BBadge>
          <BBadge variant="secondary">{{value.code}}</BBadge>
          {{ value.message}}
        </template>


      </BTable>

      <BRow>
        <BCol>
          <BPagination
            size="sm"
            v-model="logCurrPage"
            :total-rows="jobLogRows"
            :per-page="logPerPage"
            aria-controls="jobLogTable"
          ></BPagination>
        </BCol>
      </BRow>

    </BModal>


  </div>
</template>

<style src="spinkit/spinkit.min.css"></style> <!--animated processing icon-->

<script>
//-------------------------------------------------------------------------------------------------
import '@/common/HelperMixin';
import {
  apiCall,
  cloneVar,
} from '@/common/utils';
import VueCronEditorBuefy from 'vue-cron-editor-buefy';
import moment from "moment/moment";

const _config_job = {
  jobId      : 'JOB-0000',
  name       : '',
  description: '작업 설명',
  displayName: '', // 헤더 접속상태 배지 표시명. 비우면 description이 쓰인다
  enabled    : false,
  scheduled  : true,
  runSchedule : '',
  eventName  : '',
  dbType     : '5',
  serverIp   : '127.0.0.1',
  port       : 9999,
  baseUrl    : '',
  authPath   : '',
  apiPath    : '',
  siteCode   : '',
  account    : '',
  password   : '',
  authToken  : '',
  encSend    : true, // 상위서버 전송 배치(ims-*) 암호화 여부. 백엔드 config_job 기본값과 동일
  remark     : '',
  status     : '10',
  runHistory : [],
};


//----------------------------------------------------------------------------------------------------
export default {
  name: "BatchService",
  components: {
    VueCronEditorBuefy
  },
  computed: {
    jobLogRows(){
      return this.jobLogs.length;
    }
  },

  data () {
    return {

      job : null,
      jobRows: [],
      // 배치서버 실행 상태 요약 (/api/job/status)
      jobStatus: { registeredCount: 0, runningCount: 0, errorCount: 0 },
      jobStatusError: '', // 배치서버 조회 실패 사유. 설정 목록 표시는 계속되어야 하므로 별도 보관
      jobFields: [
        { key: 'jobId'       , label: '작업코드' },
        { key: 'name'        , label: '작업명' },
        { key: 'displayName' , label: '표시명' },
        { key: 'status'      , label: '상태' },
        { key: 'scheduled'   , label: '크론등록' },
        { key: 'runningSec'  , label: '실행경과' },
        { key: 'enabled'     , label: '사용여부' },
        { key: 'encSend'     , label: '전송암호화' },
        { key: 'runSchedule' , label: '작업일정' },
        // { key: 'runOnEvent'  , label: '이벤트작업' },
        // { key: 'eventName'   , label: '이벤트명' },
        // { key: 'dbType'      , label: '' },
        // { key: 'serverIp'    , label: '서버IP' },
        // { key: 'port'        , label: '' },
        // { key: 'apiUrl'      , label: '' },
        // { key: 'dbName'      , label: '' },
        // { key: 'account'     , label: '' },
        // { key: 'password'    , label: '' },
        // { key: 'authToken'   , label: '' },

        { key: 'remark'      , label: '비고' },
      ],
      expression: '*/1 * * * *',
      locale : {
        ko: {
          every: "Every",
          mminutes: "분 간격",
          hoursOnMinute: "시/분",
          daysAt: "일/시:분",
          at: "시:분",
          onThe: "일자",
          dayOfEvery: "월",
          monthsAt: "시:분",
          everyDay: "Every",
          mon: "월",
          tue: "화",
          wed: "수",
          thu: "목",
          fri: "금",
          sat: "토",
          sun: "일",
          hasToBeBetween: "Has to be between",
          and: "and",
          minutes: "분간격",
          hourly: "시간간격",
          daily: "일간",
          weekly: "주간",
          monthly: "월간",
          advanced: "커스텀설정",
          cronExpression: "크론 표현식:"
        }
      },

      isApplyJob: false,
      // packetTypeMap : this.$store.state.codeMaps['PACKET_TYPE'],
      // packetTypeOpts : this.$store.state.codeOpts['PACKET_TYPE'],
      // encTypeMap: this.$store.state.codeMaps['ENC_TYPE'],
      // encTypeOpts: this.$store.state.codeOpts['ENC_TYPE'],
      dbTypeOpts: this.$store.state.codeOpts['DB_TYPE'],

      jobOpts: [],
      jobLogVisible: false,
      jobId: '',
      jobLogs: [],
      fromDts: moment().add(-1, 'months').format('YYYY-MM-DD'),
      toDts: moment().format('YYYY-MM-DD'),

      logFields: [
        { key: 'jobId'       , label: '작업코드' },
        { key: 'name'        , label: '작업명' },
        { key: 'createdAt'   , sortable: true, label: '로그일시', formatter: v=>{return (v? moment(v).format('YYYY-MM-DD HH:mm:ss'):'')} },
        { key: 'log'        , label: '로그' },
      ],

      logPerPage: 20,
      logCurrPage: 1,
      progCount: 0,
      totalCount: 0,


    }

  },
  async created(){
    try{
      console.log("--- Batch Services mounted---------------------");
      await this.getJob();
    }catch(err){
      console.error(err);
    }
  },


  mounted() {
    console.log("--- Batch Services mounted---------------------");
    // below is not work!
  },

  methods: {
    /**
     * 작업코드 검증. 대문자 영문/숫자/'-'/'_' 조합 1~30자.
     * 로그조회 URL(/api/job/log/:jobId) 경로에 그대로 쓰이므로 URL 안전 문자로 제한한다.
     */
    isValidJobId(v){
      return /^[A-Z0-9_-]{1,30}$/.test(v || '');
    },

    /**
     * 표시명 검증. 선택 입력이므로 비어있으면 판정하지 않고(null) 20자 초과만 잡는다.
     */
    isValidDisplayName(v){
      if(!v) return null;
      return v.length <= 20;
    },

    /**
     * 서버 포트 검증. 80 ~ 65535 (well-known 이하 구간은 배치 대상이 아니다)
     */
    isValidPort(v){
      const port = Number(v);
      return Number.isInteger(port) && port >= 80 && port <= 65535;
    },

    /**
     * 목록 행 클래스. 사용 중지된 작업을 흐리게 표시해 한눈에 구분되게 한다.
     * BTable은 상세행(row-details)에도 이 함수를 호출하므로 일반 행만 처리한다.
     */
    jobRowClass(item, type){
      if(!item || type!=='row') return '';
      return item.enabled ? '' : 'text-muted';
    },

    /**
     * 상위(통합모니터링) 서버 전송 배치인지 판별한다.
     * encSend 설정은 ims-* 작업에만 의미가 있다 (백엔드 batch/jobs/ims-push-utils.js).
     */
    isPushJob(job){
      return /^ims-/.test(job?.name || '');
    },

    statusName(sts){
      switch(sts){
        case '00': return '등록';
        case '10': return '초기화';
        case '20': return '작업대기';
        case '30': return '실행중';
        case '99': return '에러';
        default: return sts;
      }
    },

    statusVariant(sts){
      switch(sts){
        case '00': return 'dark';
        case '10': return 'success';
        case '20': return 'info';
        case '30': return 'warning';
        case '99': return 'danger';
        default: return sts;
      }
    },

    async getJob(){
      // console.log('getJob called.....');
      try{
        const r = await apiCall('get', `/api/job`);
        if(r.code===200){
          this.jobOpts = [];
          this.jobRows = r.result;
          this.jobRows.map(e=>{ this.jobOpts.push({text: `[${e.jobId}] ${e.name}: ${e.description}`, value: e.jobId}) });
          await this.getJobStatus();
        }else{
          await this.alertDanger(r.message, r.code);
        }

        await this.toastResult(r, '조회', false);

      }catch(err){
        console.error( err );
        await this.alertDanger(err.message);
      }
    },

    /**
     * 배치서버로부터 작업별 실행 상태를 조회해 목록에 병합한다.
     *
     * /api/job 은 설정(config_job)만 돌려주므로 크론 등록 여부와 실행 경과는 알 수 없다.
     * 배치서버가 내려간 상태여도 설정 목록은 계속 보여야 하므로,
     * 여기서 실패해도 예외를 올리지 않고 안내 배지만 표시한다.
     */
    async getJobStatus(){
      try{
        const r = await apiCall('get', `/api/job/status`);

        if(r.code!==200 || !r.result){
          this.jobStatusError = r.message || '배치서버 상태 조회 실패';
          this.jobStatus = { registeredCount: 0, runningCount: 0, errorCount: 0 };
          return;
        }

        this.jobStatusError = '';
        this.jobStatus = {
          registeredCount: r.result.registeredCount || 0,
          runningCount   : r.result.runningCount || 0,
          errorCount     : r.result.errorCount || 0,
        };

        // jobId 기준으로 실행 상태를 합친다.
        const statusMap = {};
        (r.result.jobs || []).forEach( s => { statusMap[s.jobId] = s; } );

        // Vue 2는 기존 객체에 속성을 추가해도 반응형이 되지 않으므로 배열을 새로 만든다.
        this.jobRows = this.jobRows.map( row => {
          const s = statusMap[row.jobId];
          if(!s) return { ...row, scheduled: null, runningSec: null };
          return {
            ...row,
            scheduled : s.scheduled,
            runningSec: s.runningSec,
            status    : s.status,   // 배치서버 조회 시점이 더 최신이다
            remark    : s.remark,
          };
        });

      }catch(err){
        console.error( err );
        this.jobStatusError = err.message;
        this.jobStatus = { registeredCount: 0, runningCount: 0, errorCount: 0 };
      }
    },


    /**
     * 신규 작업코드 기본값 생성. JOB-0001 형식으로 만든다.
     * 목록 길이(+1)로 매기면 중간 삭제분 때문에 코드가 겹치므로,
     * 이미 쓰고있는 JOB-#### 중 최대 번호 다음 값을 쓴다.
     */
    nextJobId(){
      const maxNo = this.jobRows.reduce( (max, e) => {
        const m = /^JOB-(\d{4})$/.exec(e.jobId || '');
        return m ? Math.max(max, Number(m[1])) : max;
      }, 0);
      return 'JOB-' + ('0000'+(maxNo+1)).slice(-4);
    },

    async addJob(){
      this.job = cloneVar(_config_job);
      this.job.jobId = this.nextJobId();
      this.job.name = this.job.jobId + '-job-name'
      this.job.description = '작업 상세 내용'

      const r = await apiCall('post', `/api/job`, this.job);
      if(r.code===200){
        this.job = r.result;
        await this.getJob();
      }
      await this.toastResult(r);
    },


    async jobSelected(item){
      // console.log("------------ jobSelected ------------", item.length);
      if( item.length === 0 ) {
        this.job = null;
      }else{
        this.job = item.pop();
        // this.$root.$emit('bv::toggle::collapse', 'serviceCollapse');
      }
    },

    async applyJob(){
      try{
        this.isApplyJob = true;
        await this.toastInfo(`배치작업 정보를 시스템에 적용 중 입니다`);
        const r = await apiCall('get', `/api/job/apply`);
        await this.toastResult(r, `배치작업 변경 정보 시스템 적용`);
        if(r.code===200){
          await this.alertSuccess('배치작업 변경이 적용 되었습니다.');
          await this.getJob();
        }else{
          await this.alertDanger('적용 실패', r.code);
        }

      }catch(err){
        await this.alertError(err);
      }finally{
        this.isApplyJob = false;
      }
    },


    async jobSubmit(evt){
      evt.preventDefault();
      // console.log( 'jobSubmit------------->', this.service );
      try{
        const r = await apiCall('put', `/api/job/${this.job._id}`, this.job );
        // console.log('jobSubmit------------->' , r);
        await this.toastResult(r);
      }catch(err){
        console.error(err);
      }
    },


    async deleteRecord(){
      // let r = null, msg = null, notice = null;
      try{

        const obj = this.job;

        const confirmMsg = `${obj.name} 데이터를 삭제 합니다. 삭제된 데이터는 복구할 수 없으며 시스템 장애가 발생할 수 있습니다. 진행 하시겠습니까?`;

        if( !(await this.confirmModal(confirmMsg, '레코드 삭제')) ){
          return;
        }

        const r = await apiCall('DEL', `/api/job/${obj._id}`);
        // console.log( r );
        if( r.code===200 ){
          await this.getJob();
        }else{
          await this.toastInfo('삭제 에러: '+r.message, 'Error', 'danger' );
        }
      }catch(err){
        await this.alertError(err);
      }
    },


    showJobLogModal(jobId){
      this.$refs['jobLog'].show();
      this.getJobLog(jobId);
    },

    async getJobLog(jobId=''){
      try{
        this.jobLogs = [];
        this.jobId = jobId;
        const r = await apiCall('get', `/api/job/log/${jobId}?fromDts=${this.fromDts}&toDts=${this.toDts}`);
        if(r.code===200){
          this.jobLogs = r.result;
        }else{
          await this.alertDanger(r.message, r.code);
        }

        await this.toastResult(r, '조회', false);

      }catch(err){
        console.error( err );
        await this.alertDanger(err.message);
      }
    }




  }
}
</script>
